import clsx from 'clsx'
import {setIn} from 'formik'
import React, {useMemo} from 'react'
import {useIntl} from 'react-intl'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import {KTSVG} from '../../../../../_gori/helpers'
import {Button, InputTextFormik, SelectFormik} from '../../../../../_gori/partials/widgets'
import {ParcelItem} from './PackageType'
import {
  DIMENSIONS_PLACEHOLDER,
  DIMENSIONS,
  OPTION_DIMENSION_UNIT,
  OPTION_WEIGHT_UNIT,
} from '../../../../../_gori/constants'

type Props = {
  formik: any
  keyItem: string
  index: number
  disabled?: boolean
  isDisabledDimension: boolean
  isDisabledPackageCount: boolean
  oneRateUnmatched: boolean
  carriersPackage: any
  displayExtendParts: boolean
  disabledDelete: boolean
  isMobileLayout: boolean
  isInModal?: boolean
  onDimensionChange: (keyItem: string, dimKey: string, event: any) => void
  onChangeDimensionUnit: (e: {value: string}) => void
  onChangeWeightUnit: (e: {value: string}) => void
  onRemove: (key: string) => void
  customSelectInputStyle: (isDisabled?: boolean) => Record<string, unknown>
}

const PackageInfoRow: React.FC<Props> = ({
  formik,
  keyItem,
  index,
  disabled,
  isDisabledDimension,
  isDisabledPackageCount,
  oneRateUnmatched,
  carriersPackage,
  displayExtendParts,
  disabledDelete,
  isMobileLayout,
  isInModal = false,
  onDimensionChange,
  onChangeDimensionUnit,
  onChangeWeightUnit,
  onRemove,
  customSelectInputStyle,
}) => {
  const intl = useIntl()
  const parcels = formik.values.parcels
  const firstParcel = Object.values(parcels)?.[0] as ParcelItem
  const packageType = firstParcel.package_type
  const detectPackageType = carriersPackage
    ?.flatMap((carrier: any) => carrier.value.filter((item: any) => item.value === packageType))
    .find(Boolean)

  const editable = detectPackageType?.parcel?.editable

  const dimensionFormik = useMemo(() => {
    if (!oneRateUnmatched) return formik
    let errors = formik.errors
    DIMENSIONS.forEach((dimKey) => {
      errors = setIn(errors, `parcels.${keyItem}.${dimKey}`, undefined)
    })
    return {...formik, errors}
  }, [formik, oneRateUnmatched, keyItem])

  const dimensionInputs = (
    <div className='dimensions-wrap d-flex justify-content-start'>
      {DIMENSIONS.map((dimKey, dimIndex) => {
        const isFieldHidden = editable && detectPackageType?.parcel?.hidden.includes(dimKey)

        return (
          <div
            className={clsx('col-4 col-sm-4 pe-2', {'d-none': isFieldHidden})}
            key={dimKey || dimIndex}
          >
            <div className='position-relative dimension-input-wrap'>
              <InputTextFormik
                type='number'
                min={0}
                className={clsx('border-0', {'pe-none': isDisabledDimension})}
                labelClassName='text-muted'
                label=''
                formik={dimensionFormik}
                onChange={(event: any) => onDimensionChange(keyItem, dimKey, event)}
                name={`parcels.${keyItem}.${dimKey}`}
                disabled={isDisabledDimension}
                inputClassName={clsx('dimension-input rounded')}
                configAppend={{
                  name: DIMENSIONS_PLACEHOLDER[dimKey],
                  position: 'left',
                }}
              />
            </div>
          </div>
        )
      })}
    </div>
  )

  return (
    <div className='package-item my-1'>
      <div className='row'>
        {isMobileLayout ? (
          <div className='col-12 package-item-mobile'>
            {!displayExtendParts && <div className='text-muted fw-normal mb-2'>{index + 1}.</div>}
            <div className='package-mobile-group package-mobile-group--dimensions'>
              {index === 0 && (
                <div className='d-flex justify-content-between align-items-center mb-2'>
                  <div
                    className={clsx('form-label mb-0 fw-bold ps-2', {
                      'fs-7': isInModal,
                    })}
                  >
                    {intl.formatMessage({id: 'DIMENSIONS'})}{' '}
                    <span className='fw-light'>{'L*W*H'}</span>
                  </div>
                  <SelectFormik
                    className={clsx('form-label mb-0 dimension-unit-select', {
                      'pe-none': disabled,
                      'fs-7': isInModal,
                    })}
                    placeholder=''
                    label=''
                    labelClassName='text-muted'
                    options={OPTION_DIMENSION_UNIT}
                    name='dimension_unit'
                    formik={formik}
                    disabled={isDisabledDimension}
                    emptyDefault={false}
                    onChange={(e) => onChangeDimensionUnit(e)}
                    hideIndicatorSeparator
                    isSearchable={false}
                    {...customSelectInputStyle(isDisabledDimension)}
                  />
                </div>
              )}
              {dimensionInputs}
            </div>
            <div className='d-flex flex-wrap mt-2'>
              <div className='col-12 col-sm-6 pe-2 package-mobile-group package-mobile-group--weight'>
                {index === 0 && (
                  <div
                    className='d-flex justify-content-between align-items-center mb-2'
                    style={{height: '40px'}}
                  >
                    <div className={clsx('fw-bold form-label mb-0', {'fs-7': isInModal})}>
                      {intl.formatMessage({id: 'WEIGHT'})}
                    </div>
                    <SelectFormik
                      className={clsx('form-label mb-0 weight-unit-select', {
                        'pe-none': disabled,
                        'fs-7': isInModal,
                      })}
                      label=''
                      placeholder=''
                      labelClassName='text-muted bg-light'
                      options={OPTION_WEIGHT_UNIT}
                      name='weight_unit'
                      formik={formik}
                      disabled={disabled}
                      emptyDefault={false}
                      onChange={(e) => onChangeWeightUnit(e)}
                      hideIndicatorSeparator
                      isSearchable={false}
                      {...customSelectInputStyle(disabled)}
                    />
                  </div>
                )}
                <div className='weight-wrap'>
                  <InputTextFormik
                    type='number'
                    min={0}
                    className={clsx('', {'pe-none': disabled})}
                    labelClassName='text-muted'
                    label=''
                    formik={formik}
                    name={`parcels.${keyItem}.weight`}
                    disabled={disabled}
                  />
                </div>
              </div>
              <div className='col-12 col-sm-6 ps-2 package-mobile-group package-mobile-group--count'>
                {index === 0 && (
                  <div
                    className={clsx(
                      'form-label mb-0 fw-bold d-flex justify-content-start align-items-center mb-2',
                      {'fs-7': isInModal}
                    )}
                    style={{height: '40px'}}
                  >
                    {intl.formatMessage({id: 'PACKAGE_COUNT'})}
                    <OverlayTrigger
                      overlay={
                        <Tooltip id='tooltip-disabled'>
                          {intl.formatMessage({id: 'PACKAGE_COUNT_TOOLTIP'})}
                        </Tooltip>
                      }
                    >
                      <span className='d-flex justify-content-start align-items-start'>
                        <KTSVG
                          path='/media/gori/orders/info.svg'
                          className='ms-1 mb-1 svg-icon-4 cursor-pointer'
                        />
                      </span>
                    </OverlayTrigger>
                  </div>
                )}
                <div className='d-flex align-items-center'>
                  <div className='flex-fill'>
                    <InputTextFormik
                      type='number'
                      min={0}
                      className={clsx('border-0', {'pe-none': isDisabledPackageCount})}
                      labelClassName='text-muted'
                      label=''
                      formik={formik}
                      name={`parcels.${keyItem}.package_count`}
                      disabled={isDisabledPackageCount}
                      isInteger={true}
                    />
                  </div>
                  {!displayExtendParts && (
                    <Button
                      event={() => onRemove(keyItem)}
                      className={clsx('text-hover-danger border-0 bg-transparent ms-1', {
                        'pe-none opacity-50': disabledDelete,
                      })}
                      disabled={disabledDelete}
                    >
                      <span className='fs-4 text-gray-600'>
                        <KTSVG path='/media/gori/orders/trash.svg' className='svg-icon-1' />
                      </span>
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className='col-12 d-flex flex-wrap'>
            <div className='col-6 col-sm-6 pe-3 d-flex'>
              <div
                className={clsx(
                  'col-1 text-muted m-0 fw-normal text-end d-flex justify-content-center align-self-start pt-4',
                  {'d-none': displayExtendParts}
                )}
              >
                {index + 1}.
              </div>
              <div className={displayExtendParts ? 'col-12' : 'col-11'}>{dimensionInputs}</div>
            </div>
            <div className='col-3 col-sm-3 pe-3'>
              <div className='weight-wrap'>
                <InputTextFormik
                  type='number'
                  min={0}
                  className={clsx('', {'pe-none': disabled})}
                  labelClassName='text-muted'
                  label=''
                  formik={formik}
                  name={`parcels.${keyItem}.weight`}
                  disabled={disabled}
                />
              </div>
            </div>
            <div className='col-3 col-sm-3 ps-2'>
              <div className='col-12 d-flex justify-content-center align-items-center'>
                <div className={displayExtendParts ? 'col-12' : 'col-10'}>
                  <InputTextFormik
                    type='number'
                    min={0}
                    className={clsx('border-0', {'pe-none': isDisabledPackageCount})}
                    labelClassName='text-muted'
                    label=''
                    formik={formik}
                    name={`parcels.${keyItem}.package_count`}
                    disabled={isDisabledPackageCount}
                    isInteger={true}
                  />
                </div>
                <div
                  className={clsx('col-2 pt-2 align-self-start', {
                    'd-none': displayExtendParts,
                  })}
                >
                  <Button
                    event={() => onRemove(keyItem)}
                    className={clsx('text-hover-danger border-0 bg-transparent', {
                      'pe-none opacity-50': disabledDelete,
                    })}
                    disabled={disabledDelete}
                  >
                    <span className='fs-4 text-gray-600'>
                      <KTSVG path='/media/gori/orders/trash.svg' className='svg-icon-1' />
                    </span>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export {PackageInfoRow}
